'use client'

import { IconCheck, IconCreditCard, IconLoader } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useStorePaymentMethod } from '@/features/payment-methods/hooks/use-store-payment-method'
import { useStoreSlug } from '@/hooks/use-store-slug'
import { useStores } from '@/hooks/use-stores'

export const Route = createFileRoute('/$store/_layout/settings_/payment')({
  component: StorePaymentSettingsPage,
})

function StorePaymentSettingsPage() {
  const storeSlug = useStoreSlug()
  const { stores, isLoading: isLoadingStores } = useStores()
  const [saved, setSaved] = React.useState(false)

  const currentStore = React.useMemo(() => {
    if (!storeSlug) return null
    return stores.find((store) => store.slug === storeSlug) || null
  }, [storeSlug, stores])

  const {
    availableMethods,
    selectedMethodId,
    setSelectedMethodId,
    infinitePayHandle,
    setInfinitePayHandle,
    isInfinitePaySelected,
    isLoading,
    isSaving,
    save,
  } = useStorePaymentMethod(currentStore?.id)

  async function handleSave() {
    await save()
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  if (isLoadingStores || isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <IconLoader className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!currentStore) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Loja não encontrada.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Cobrança</h1>
          <p className="text-sm text-muted-foreground">
            Escolha o método de pagamento e configure os dados da loja
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={
            isSaving || (isInfinitePaySelected && !infinitePayHandle.trim())
          }
        >
          {saved ? (
            <>
              <IconCheck className="mr-1.5 size-4" />
              Salvo!
            </>
          ) : isSaving ? (
            <>
              <IconLoader className="mr-1.5 size-4 animate-spin" />
              Salvando...
            </>
          ) : (
            'Salvar'
          )}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconCreditCard className="size-5" />
            Método de pagamento
          </CardTitle>
          <CardDescription>
            Selecione um método para receber pedidos nesta loja.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3">
            {availableMethods.map((method) => (
              <button
                type="button"
                key={method.id}
                onClick={() => setSelectedMethodId(method.id)}
                className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors ${
                  selectedMethodId === method.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <div
                  className={`mt-0.5 size-4 shrink-0 rounded-full border-2 ${
                    selectedMethodId === method.id
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground'
                  }`}
                />
                <div className="flex flex-1 flex-col gap-0.5">
                  <span className="font-medium">{method.name}</span>
                  <span className="text-sm text-muted-foreground">
                    Slug: {method.slug}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {availableMethods.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhum método disponível. Cadastre no painel de admin.
            </p>
          )}

          {isInfinitePaySelected && (
            <div className="space-y-2 rounded-lg border border-border p-4">
              <Label htmlFor="infinitepay-handle">
                Tag InfinitePay (handle)
              </Label>
              <Input
                id={`infinitepay-handle-${currentStore.id}`}
                value={infinitePayHandle}
                onChange={(event) => setInfinitePayHandle(event.target.value)}
                placeholder="Ex.: @minhaloja ou handle da InfinitePay"
              />
              <p className="text-xs text-muted-foreground">
                Esse valor será enviado no checkout para criar o pagamento na
                InfinitePay.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
